import PropTypes from 'prop-types';
import styled from 'styled-components';

/**
 * Container Component - A simple, reusable container for displaying content
 * @param {Object} children - The children of the container
 * @param {string} $display - The display of the container
 * @param {string} $margin - The margin of the container
 * @param {string} $marginTop - The margin top of the container
 * @param {string} $marginRight - The margin right of the container
 * @param {string} $marginBottom - The margin bottom of the container
 * @param {string} $marginLeft - The margin left of the container
 * @param {string} $padding - The padding of the container
 * @param {string} $paddingTop - The padding top of the container
 * @param {string} $paddingRight - The padding right of the container
 * @param {string} $paddingBottom - The padding bottom of the container
 * @param {string} $paddingLeft - The padding left of the container
 * @param {string} $gap - The gap of the container
 * @param {string} $width - The width of the container
 * @param {string} $height - The height of the container
 * @param {string} $minWidth - The min width of the container
 * @param {string} $minHeight - The min height of the container
 * @param {string} $maxWidth - The max width of the container
 * @param {string} $maxHeight - The max height of the container
 * @param {string} $flex - The flex of the container
 * @param {string} $flexDirection - The flex direction of the container
 * @param {string} $alignItems - The align items of the container
 * @param {string} $justifyContent - The justify content of the container
 * @param {string} $flexWrap - The flex wrap of the container
 * @param {string} $backgroundColor - The background color of the container
 * @param {string} $color - The color of the container
 * @param {string} $border - The border of the container
 * @param {string} $borderRadius - The border radius of the container
 * @param {string} $borderTop - The border top of the container
 * @param {string} $borderRight - The border right of the container
 * @param {string} $borderBottom - The border bottom of the container
 * @param {string} $borderLeft - The border left of the container
 * @param {string} $position - The position of the container
 * @param {string} $top - The top of the container
 * @param {string} $right - The right of the container
 * @param {string} $bottom - The bottom of the container
 * @param {string} $left - The left of the container
 * @param {string} $zIndex - The z index of the container
 * @param {string} $overflow - The overflow of the container
 * @param {string} $cursor - The cursor of the container
 * @param {string} $opacity - The opacity of the container
 * @param {Object} props - The props of the container   
 */

export const Container = ({
    children,
    $display = 'block',
    $margin,
    $marginTop,
    $marginRight,
    $marginBottom,
    $marginLeft,
    $padding,
    $paddingTop,
    $paddingRight,
    $paddingBottom,
    $paddingLeft,
    $gap,
    $width,
    $height,
    $minWidth,
    $minHeight,
    $maxWidth,
    $maxHeight,
    $flex,
    $flexDirection = 'row',
    $alignItems,
    $justifyContent,
    $flexWrap,
    $backgroundColor,
    $color,
    $border,
    $borderRadius,
    $borderTop,
    $borderRight,
    $borderBottom,
    $borderLeft,
    $position,
    $top,
    $right,
    $bottom,
    $left,
    $zIndex,
    $overflow,
    $cursor,
    $opacity,
    ...props
}) => {
    return (
        <StyledContainer
            $display={$display}
            $margin={$margin}
            $marginTop={$marginTop}
            $marginRight={$marginRight}
            $marginBottom={$marginBottom}
            $marginLeft={$marginLeft}
            $padding={$padding}
            $paddingTop={$paddingTop}
            $paddingRight={$paddingRight}
            $paddingBottom={$paddingBottom}
            $paddingLeft={$paddingLeft}
            $gap={$gap}
            $width={$width}
            $height={$height}
            $minWidth={$minWidth}
            $minHeight={$minHeight}
            $maxWidth={$maxWidth}
            $maxHeight={$maxHeight}
            $flex={$flex}
            $flexDirection={$flexDirection}
            $alignItems={$alignItems}
            $justifyContent={$justifyContent}
            $flexWrap={$flexWrap}
            $backgroundColor={$backgroundColor}
            $color={$color}
            $border={$border}
            $borderRadius={$borderRadius}
            $borderTop={$borderTop}
            $borderRight={$borderRight}
            $borderBottom={$borderBottom}
            $borderLeft={$borderLeft}
            $position={$position}
            $top={$top}
            $right={$right}
            $bottom={$bottom}
            $left={$left}
            $zIndex={$zIndex}
            $overflow={$overflow}
            $cursor={$cursor}
            $opacity={$opacity}
            {...props}
        >
            {children}
        </StyledContainer>
    );
};

export const StyledContainer = styled.div`
    display: ${({ $display }) => $display || 'block'};
    
    ${({ $margin }) => $margin && `margin: ${$margin};`}
    ${({ $marginTop }) => $marginTop && `margin-top: ${$marginTop};`}
    ${({ $marginRight }) => $marginRight && `margin-right: ${$marginRight};`}
    ${({ $marginBottom }) => $marginBottom && `margin-bottom: ${$marginBottom};`}
    ${({ $marginLeft }) => $marginLeft && `margin-left: ${$marginLeft};`}
    
    ${({ $padding }) => $padding && `padding: ${$padding};`}
    ${({ $paddingTop }) => $paddingTop && `padding-top: ${$paddingTop};`}
    ${({ $paddingRight }) => $paddingRight && `padding-right: ${$paddingRight};`}
    ${({ $paddingBottom }) => $paddingBottom && `padding-bottom: ${$paddingBottom};`}
    ${({ $paddingLeft }) => $paddingLeft && `padding-left: ${$paddingLeft};`}
    
    ${({ $gap }) => $gap && `gap: ${$gap};`}
    
    ${({ $width }) => $width && `width: ${$width};`}
    ${({ $height }) => $height && `height: ${$height};`}
    ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
    ${({ $minHeight }) => $minHeight && `min-height: ${$minHeight};`}
    ${({ $maxWidth }) => $maxWidth && `max-width: ${$maxWidth};`}
    ${({ $maxHeight }) => $maxHeight && `max-height: ${$maxHeight};`}
    
    ${({ $flex }) => $flex && `flex: ${$flex};`}
    ${({ $flexDirection }) => $flexDirection && `flex-direction: ${$flexDirection};`}
    ${({ $alignItems }) => $alignItems && `align-items: ${$alignItems};`}
    ${({ $justifyContent }) => $justifyContent && `justify-content: ${$justifyContent};`}
    ${({ $flexWrap }) => $flexWrap && `flex-wrap: ${$flexWrap};`}
    
    ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`}
    ${({ $color }) => $color && `color: ${$color};`}
    
    ${({ $border }) => $border && `border: ${$border};`}
    ${({ $borderRadius }) => $borderRadius && `border-radius: ${$borderRadius};`}
    ${({ $borderTop }) => $borderTop && `border-top: ${$borderTop};`}
    ${({ $borderRight }) => $borderRight && `border-right: ${$borderRight};`}
    ${({ $borderBottom }) => $borderBottom && `border-bottom: ${$borderBottom};`}
    ${({ $borderLeft }) => $borderLeft && `border-left: ${$borderLeft};`}
    
    ${({ $position }) => $position && `position: ${$position};`}
    ${({ $top }) => $top && `top: ${$top};`}
    ${({ $right }) => $right && `right: ${$right};`}
    ${({ $bottom }) => $bottom && `bottom: ${$bottom};`}
    ${({ $left }) => $left && `left: ${$left};`}
    ${({ $zIndex }) => $zIndex && `z-index: ${$zIndex};`}
    
    ${({ $overflow }) => $overflow && `overflow: ${$overflow};`}
    ${({ $cursor }) => $cursor && `cursor: ${$cursor};`}
    ${({ $opacity }) => $opacity !== undefined && `opacity: ${$opacity};`}
`;

Container.propTypes = {
    children: PropTypes.node.isRequired,
    $display: PropTypes.string,
    $margin: PropTypes.string,
    $marginTop: PropTypes.string,
    $marginRight: PropTypes.string,
    $marginBottom: PropTypes.string,
    $marginLeft: PropTypes.string,
    $padding: PropTypes.string,
    $paddingTop: PropTypes.string,
    $paddingRight: PropTypes.string,
    $paddingBottom: PropTypes.string,
    $paddingLeft: PropTypes.string,
    $gap: PropTypes.string,
    $width: PropTypes.string,
    $height: PropTypes.string,
    $minWidth: PropTypes.string,
    $minHeight: PropTypes.string,
    $maxWidth: PropTypes.string,
    $maxHeight: PropTypes.string,
    $flex: PropTypes.string,
    $flexDirection: PropTypes.string,
    $alignItems: PropTypes.string,
    $justifyContent: PropTypes.string,
    $flexWrap: PropTypes.string,
    $backgroundColor: PropTypes.string,
    $color: PropTypes.string,
    $border: PropTypes.string,
    $borderRadius: PropTypes.string,
    $borderTop: PropTypes.string,
    $borderRight: PropTypes.string,
    $borderBottom: PropTypes.string,
};

